import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Inbox, ShoppingBag, Package, Settings as SettingsIcon, Wand2, LogOut, ArrowLeft, Home, Users as UsersIcon, Briefcase, Map as MapIcon, Boxes, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ALL_TABS = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, roles: ["technician", "admin", "owner"] },
  { to: "/dashboard/jobs", label: "My Jobs", icon: Briefcase, roles: ["technician", "admin", "owner"] },
  { to: "/dashboard/map", label: "Map", icon: MapIcon, roles: ["admin", "owner"], openInNewWindow: true },
  { to: "/dashboard/setup", label: "Setup Wizard", icon: Wand2, roles: ["owner"] },
  { to: "/dashboard/quotes", label: "Quotes", icon: Inbox, roles: ["admin", "owner"] },
  { to: "/dashboard/orders", label: "Orders", icon: ShoppingBag, roles: ["admin", "owner"] },
  { to: "/dashboard/products", label: "Products", icon: Package, roles: ["admin", "owner"] },
  { to: "/dashboard/inventory", label: "Inventory", icon: Boxes, roles: ["admin", "owner"] },
  { to: "/dashboard/users", label: "Users", icon: UsersIcon, roles: ["admin", "owner"] },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, roles: ["owner"] },
];

function openMapWindow(to) {
  // Try a real popup window with reasonable defaults; if a popup blocker
  // intercepts, fall back to opening in a new tab.
  const w = window.open(
    to,
    "fitzpatrick-map",
    "popup=yes,width=1200,height=820,noopener,noreferrer"
  );
  if (!w) window.open(to, "_blank", "noopener,noreferrer");
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const tabs = ALL_TABS.filter((t) => t.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:border-cyan-300 hover:text-cyan-700" data-testid="dashboard-exit-to-site-link">
              <Home className="h-4 w-4" /> Back to site
            </Link>
            <span className="hidden text-xs uppercase tracking-widest text-slate-400 md:inline">
              {user?.role === "owner" ? "Owner Console" : user?.role === "admin" ? "Admin Console" : "Technician Console"}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="hidden truncate text-slate-500 md:inline">{user?.email}</span>
            <button onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-slate-600 hover:bg-slate-100" data-testid="dashboard-topbar-logout-button">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1400px] gap-6 px-4 py-6 md:px-8">
        <aside className="hidden w-64 shrink-0 md:block">
          <nav className="dash-surface p-2">
            {tabs.map((t) => (
              t.openInNewWindow ? (
                <button
                  key={t.to}
                  type="button"
                  onClick={() => openMapWindow(t.to)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                  data-testid={`dashboard-nav-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                  title="Opens in a new window"
                >
                  <t.icon className="h-4 w-4" /> {t.label}
                  <ExternalLink className="ml-auto h-3 w-3 text-slate-400" />
                </button>
              ) : (
                <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid={`dashboard-nav-${t.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <t.icon className="h-4 w-4" /> {t.label}
                </NavLink>
              )
            ))}
            <Link to="/" className="mt-2 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-50" data-testid="dashboard-sidebar-exit-link">
              <ArrowLeft className="h-4 w-4" /> Exit to website
            </Link>
          </nav>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="mb-4 flex gap-2 overflow-x-auto md:hidden">
            {tabs.map((t) => (
              t.openInNewWindow ? (
                <button
                  key={t.to}
                  type="button"
                  onClick={() => openMapWindow(t.to)}
                  className="whitespace-nowrap rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
                  data-testid={`dashboard-mobile-nav-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {t.label} ↗
                </button>
              ) : (
                <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-blue-600 text-white" : "bg-white text-slate-700 border border-slate-200"}`}>
                  {t.label}
                </NavLink>
              )
            ))}
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
